import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import DebouncedInput from "./debouncedInput";
import DownloadExcel from "./downloadExcel";

export function DataTable({ data, columns, month, userNome, children }) {
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState([]);

  const fileName =
    userNome && userNome.trim() !== ""
      ? `Relatório de Produtividade ${month} - ${userNome}`
      : `Relatório de Produtividade ${month}`;

  const tableState = children ? { sorting, globalFilter } : { sorting };

  const processedData = useMemo(
    () =>
      data.map((row) => ({
        ...row,
        nome_epico: row.nome_epico ?? "",
      })),
    [data]
  );

  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => !!children,
    state: tableState,
    onSortingChange: setSorting,
    ...(children && {
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
    }),
  });

  const handleGlobalFilterChange = useCallback(
    (value) => setGlobalFilter(value),
    []
  );

  return (
    <div>
      {/* Barra de pesquisar */}
      {children && (
        <div className="flex justify-between mb-2 items-center gap-10">
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={handleGlobalFilterChange}
            placeholder="Pesquisar..."
          />
          <DownloadExcel
            data={data}
            fileName={fileName}
          />
        </div>
      )}

      {/* Tabela externa */}
      <div
        className={`rounded-md overflow-auto w-full max-h-96 ${
          children && "border border-zinc-200"
        }`}
      >
        <Table className={`${children ? "w-full" : "w-[90%] mx-auto"}`}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="cursor-pointer"
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {/* Nome da coluna */}
                      {header.isPlaceholder ? null : (
                        <span
                          className={`text-sm font-medium ${
                            children ? "text-zinc-500" : "text-gray-700"
                          }`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                      )}
                      {/* Ícone de ordenação */}
                      {header.column.getCanSort() && <ArrowUpDown size={16} />}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <TableRow className="gap-1">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                {/* Tabela interna */}
                {row.getIsExpanded() && children && (
                  <>
                    <TableRow>
                      <TableCell colSpan={row.getVisibleCells().length}>
                        {children(row.original)}
                      </TableCell>
                    </TableRow>
                    <TableRow className="text-right text-sm font-medium text-zinc-500">
                      <TableCell
                        colSpan={row.getVisibleCells().length - 3}
                      ></TableCell>
                      <TableCell>
                        <div>
                          <p>Total horas estimadas:</p>
                          <p>
                            {row.original.historias_de_usuario
                              .filter((h) => h.tempo_esperado !== null)
                              .reduce(
                                (total, historia) =>
                                  total + historia.tempo_esperado,
                                0
                              )}{" "}
                            horas
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p>Total horas realizadas:</p>
                          <p>
                            {row.original.historias_de_usuario
                              .filter((h) => h.tempo_gasto !== null)
                              .reduce(
                                (total, historia) =>
                                  total + historia.tempo_gasto,
                                0
                              )}{" "}
                            horas
                          </p>
                        </div>
                      </TableCell>
                      <TableCell colSpan={1}></TableCell>
                    </TableRow>
                  </>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {children && (
        <div className="mt-4 flex items-center justify-end gap-4">
          <div className="flex items-center justify-center gap-2">
            <div className="h-5 w-28 items-center">
              <p className="font-medium text-sm text-nowrap">
                Linhas por página
              </p>
            </div>
            {/* Controlador de paginação */}
            <div className="h-9 w-20 items-center justify-center">
              <Select
                value={String(table.getState().pagination.pageSize)}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="10" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <SelectItem key={pageSize} value={String(pageSize)}>
                        {pageSize}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-row gap-1 items-center justify-center">
            <Button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              variant="ghost"
              size="lg"
            >
              <ChevronLeft size={20} /> Anterior
            </Button>
            {
              // [
              //   table.getState().pagination.pageIndex - 1,
              //   table.getState().pagination.pageIndex,
              //   table.getState().pagination.pageIndex + 1,
              // ]
              Array.from({ length: 5 }, (_, i) => {
                const pageIndex = table.getState().pagination.pageIndex + i - 2;
                return pageIndex;
              })
                .filter(
                  (pageIndex) =>
                    pageIndex >= 0 && pageIndex < table.getPageCount()
                )
                .map((pageIndex) => (
                  <Button
                    key={pageIndex}
                    variant={
                      table.getState().pagination.pageIndex === pageIndex
                        ? "default"
                        : "ghost"
                    }
                    onClick={() => table.setPageIndex(pageIndex)}
                    className="transition-all duration-300 ease-in-out will-change-transform"
                  >
                    {pageIndex + 1}
                  </Button>
                ))
            }
            <Button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              variant="ghost"
              size="lg"
            >
              Próximo <ChevronRight size={20} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
